import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useJobs } from '../contexts/JobContext';
import { JobCard } from '../components/JobCard';
import { AddCostsModal } from '../components/AddCostsModal';
import { CallsModal } from '../components/CallsModal';
import { MessagesModal } from '../components/MessagesModal';
import { LogOutIcon, DollarSignIcon, TrendingUpIcon, ClockIcon, BarChart3Icon, UserIcon, CheckCircleIcon } from 'lucide-react';
import { JobStatus, JobCost } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
type NotificationType = 'quote' | 'invoice' | null;
export function AdminDashboard() {
  const {
    logout,
    updateUser
  } = useAuth();
  const {
    jobs,
    updateJob
  } = useJobs();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<JobStatus | 'all'>('all');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [showCostsModal, setShowCostsModal] = useState(false);
  const [showCallsModal, setShowCallsModal] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [notification, setNotification] = useState<NotificationType>(null);
  const filteredJobs = filter === 'all' ? jobs : jobs.filter(j => j.status === filter);
  const stats = {
    total: jobs.length,
    requested: jobs.filter(j => j.status === 'requested').length,
    active: jobs.filter(j => ['scheduled', 'in-progress'].includes(j.status)).length,
    completed: jobs.filter(j => j.status === 'complete' || j.status === 'paid').length,
    revenue: jobs.reduce((sum, j) => sum + (j.revenue || 0), 0),
    costs: jobs.reduce((sum, j) => sum + j.costs.reduce((s, c) => s + c.amount, 0), 0)
  };
  const profit = stats.revenue - stats.costs;
  const switchToCustomer = () => {
    if (updateUser) {
      updateUser({
        role: 'customer'
      });
      navigate('/');
    }
  };
  const handleAddCosts = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowCostsModal(true);
  };
  const handleViewCalls = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowCallsModal(true);
  };
  const handleViewMessages = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowMessagesModal(true);
  };
  const handleCreateQuote = () => {
    setNotification('quote');
    setTimeout(() => setNotification(null), 3000);
  };
  const handleCreateInvoice = () => {
    setNotification('invoice');
    setTimeout(() => setNotification(null), 3000);
  };
  const handleSaveCosts = (newCosts: Omit<JobCost, 'id' | 'date'>[]) => {
    if (!selectedJobId) return;
    const job = jobs.find(j => j.id === selectedJobId);
    if (!job) return;
    const costsWithIds: JobCost[] = newCosts.map(cost => ({
      ...cost,
      id: `${Date.now()}-${Math.random()}`,
      date: new Date().toISOString()
    }));
    updateJob(selectedJobId, {
      costs: [...job.costs, ...costsWithIds]
    });
    setSelectedJobId(null);
  };
  const selectedJob = selectedJobId ? jobs.find(j => j.id === selectedJobId) : null;
  return <div className="min-h-screen bg-slate-50 pb-6" data-id="element-250">
      {/* Success Notification */}
      <AnimatePresence data-id="element-251">
        {notification && <motion.div initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -50
      }} className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-white rounded-xl shadow-2xl border-2 border-green-200 px-6 py-4 flex items-center gap-3 max-w-md" data-id="element-252">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.1,
          type: 'spring',
          stiffness: 500,
          damping: 15
        }} className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-253">
              <CheckCircleIcon className="w-6 h-6 text-white" data-id="element-254" />
            </motion.div>
            <div data-id="element-255">
              <p className="font-bold text-slate-900" data-id="element-256">
                {notification === 'quote' ? 'Quote' : 'Invoice'} Created!
              </p>
              <p className="text-sm text-slate-600" data-id="element-257">
                Draft saved to your billing system
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>

      <AddCostsModal isOpen={showCostsModal} onClose={() => {
      setShowCostsModal(false);
      setSelectedJobId(null);
    }} onSave={handleSaveCosts} jobTitle={selectedJob?.title || ''} data-id="element-258" />

      <CallsModal isOpen={showCallsModal} onClose={() => {
      setShowCallsModal(false);
      setSelectedJobId(null);
    }} calls={selectedJob?.phoneCalls || []} jobTitle={selectedJob?.title || ''} customerName={selectedJob?.customerName || ''} data-id="element-259" />

      <MessagesModal isOpen={showMessagesModal} onClose={() => {
      setShowMessagesModal(false);
      setSelectedJobId(null);
    }} messages={selectedJob?.messages || []} jobTitle={selectedJob?.title || ''} customerName={selectedJob?.customerName || ''} data-id="element-260" />

      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 pb-8" data-id="element-261">
        <div className="flex items-center justify-between mb-6" data-id="element-262">
          <div data-id="element-263">
            <h1 className="text-2xl font-bold mb-1" data-id="element-264">Admin Dashboard</h1>
            <p className="text-slate-300" data-id="element-265">Merem Blasting</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-266">
            <motion.button whileTap={{
            scale: 0.9
          }} onClick={switchToCustomer} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" title="Switch to Customer View" data-id="element-267">
              <UserIcon className="w-6 h-6" data-id="element-268" />
            </motion.button>
            <motion.button whileTap={{
            scale: 0.9
          }} onClick={logout} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-269">
              <LogOutIcon className="w-6 h-6" data-id="element-270" />
            </motion.button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-271">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.1
        }} className="bg-slate-800 rounded-xl p-4" data-id="element-272">
            <div className="flex items-center gap-2 mb-2" data-id="element-273">
              <ClockIcon className="w-4 h-4 text-orange-400" data-id="element-274" />
              <p className="text-slate-400 text-xs font-semibold" data-id="element-275">Pending</p>
            </div>
            <p className="text-2xl font-bold" data-id="element-276">{stats.requested}</p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.15
        }} className="bg-slate-800 rounded-xl p-4" data-id="element-277">
            <div className="flex items-center gap-2 mb-2" data-id="element-278">
              <TrendingUpIcon className="w-4 h-4 text-blue-400" data-id="element-279" />
              <p className="text-slate-400 text-xs font-semibold" data-id="element-280">Active</p>
            </div>
            <p className="text-2xl font-bold" data-id="element-281">{stats.active}</p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl p-4" data-id="element-282">
          <div className="flex items-center gap-2 mb-2" data-id="element-283">
            <DollarSignIcon className="w-5 h-5" data-id="element-284" />
            <p className="text-orange-100 text-sm font-semibold" data-id="element-285">Net Profit</p>
          </div>
          <p className="text-3xl font-bold mb-1" data-id="element-286">${profit.toLocaleString()}</p>
          <div className="flex items-center gap-4 text-xs text-orange-100" data-id="element-287">
            <span data-id="element-288">Revenue: ${stats.revenue.toLocaleString()}</span>
            <span data-id="element-289">Costs: ${stats.costs.toLocaleString()}</span>
          </div>
        </motion.div>

        {/* Analytics Button */}
        <motion.button initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} whileTap={{
        scale: 0.98
      }} onClick={() => navigate('/admin/analytics')} className="w-full mt-4 bg-slate-700 hover:bg-slate-600 text-white rounded-xl p-4 flex items-center justify-between transition-colors" data-id="element-290">
          <div className="flex items-center gap-3" data-id="element-291">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center" data-id="element-292">
              <BarChart3Icon className="w-5 h-5 text-white" data-id="element-293" />
            </div>
            <div className="text-left" data-id="element-294">
              <p className="font-bold" data-id="element-295">View Analytics</p>
              <p className="text-xs text-slate-300" data-id="element-296">
                {stats.completed} completed jobs
              </p>
            </div>
          </div>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-297">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-298" />
          </svg>
        </motion.button>
      </motion.div>

      {/* Filters */}
      <div className="px-6 py-4 overflow-x-auto" data-id="element-299">
        <div className="flex gap-2 min-w-max" data-id="element-300">
          {(['all', 'requested', 'scheduled', 'in-progress', 'complete', 'paid'] as const).map((status, index) => <motion.button key={status} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3 + index * 0.05
        }} whileTap={{
          scale: 0.95
        }} onClick={() => setFilter(status)} className={`px-4 py-2 rounded-lg font-semibold text-sm capitalize whitespace-nowrap transition-all ${filter === status ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/30' : 'bg-white text-slate-600 border-2 border-slate-200'}`} data-id="element-301">
              {status === 'in-progress' ? 'In Progress' : status}
            </motion.button>)}
        </div>
      </div>

      {/* Jobs List */}
      <div className="px-6 space-y-4" data-id="element-302">
        {filteredJobs.length === 0 ? <div className="text-center py-12" data-id="element-303">
            <p className="text-slate-500" data-id="element-304">No jobs found</p>
          </div> : filteredJobs.map((job, index) => <motion.div key={job.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4 + index * 0.05
      }} data-id="element-305">
              <JobCard job={job} onClick={() => navigate(`/admin/job/${job.id}`)} onAddCosts={() => handleAddCosts(job.id)} onViewCalls={() => handleViewCalls(job.id)} onViewMessages={() => handleViewMessages(job.id)} onCreateQuote={handleCreateQuote} onCreateInvoice={handleCreateInvoice} showAdminActions={true} data-id="element-306" />
            </motion.div>)}
      </div>
    </div>;
}